import type { Atlas, PoseEntry } from './types';

/**
 * Loads and validates the generated sprite atlas.
 *
 * The atlas is produced by scripts/build_buntu_sprites.py and committed under
 * public/buntu/, so a failure here means the assets are missing rather than
 * stale -- the caller should degrade silently rather than surface an error.
 */
export async function loadAtlas(url: string): Promise<Atlas> {
  const res = await fetch(url, { cache: 'force-cache' });
  if (!res.ok) throw new Error(`buntu: atlas ${res.status}`);
  const data = (await res.json()) as Atlas;
  if (!data?.head?.src || !data?.poses || !data?.frame) {
    throw new Error('buntu: malformed atlas');
  }
  return data;
}

/** Poses the engine needs; anything missing falls back to `idle`. */
export function pose(atlas: Atlas, name: string): PoseEntry {
  return atlas.poses[name] ?? atlas.poses.idle;
}

/**
 * Decodes every image the engine can display, up front.
 *
 * Buntu swaps poses mid-animation, and a cold <img> swap would flash a blank
 * frame, so decoding is forced before the first render.
 */
export async function preload(
  atlas: Atlas,
  assetUrl: (src: string) => string,
  names: string[]
): Promise<void> {
  const srcs = [atlas.head.src, ...names.map((n) => pose(atlas, n)?.src)];
  await Promise.all(
    Array.from(new Set(srcs.filter(Boolean))).map(
      (src) =>
        new Promise<void>((resolve) => {
          const img = new Image();
          // A missing sprite must not stall startup.
          img.onload = () => resolve();
          img.onerror = () => resolve();
          img.src = assetUrl(src);
        })
    )
  );
}
